import { Injectable } from '@angular/core';

interface RewriteRule {
  legacyPrefixes: string[];
  cdnPrefix: string;
}

/**
 * Temporary compatibility shim that rewrites legacy S3 URLs to CloudFront.
 * Remove this together with {@link CdnUrlInterceptor} once the backend/database
 * starts emitting CDN URLs directly.
 */
@Injectable({
  providedIn: 'root',
})
export class CdnUrlService {
  private readonly rewriteRules: RewriteRule[] = [
    {
      legacyPrefixes: [
        'https://simplied-public.s3.ap-southeast-1.amazonaws.com',
      ],
      cdnPrefix: 'https://files.simpliedtech.com',
    },
    {
      legacyPrefixes: [
        'https://simplied-public-default.s3.ap-southeast-1.amazonaws.com',
      ],
      cdnPrefix: 'https://assets.simpliedtech.com',
    },
    {
      legacyPrefixes: [
        'https://simplied-public-prd-invoice-new.s3.me-central-1.amazonaws.com',
      ],
      cdnPrefix: 'https://cdn.simpliedtech.com',
    },
    {
      legacyPrefixes: [
        'https://simplied-public-prd-invoice.s3.ap-southeast-1.amazonaws.com',
      ],
      cdnPrefix: 'https://invoices.simpliedtech.com',
    },
    {
      legacyPrefixes: [
        'https://simplied-public-temp.s3.ap-southeast-1.amazonaws.com',
      ],
      cdnPrefix: 'https://temp.simpliedtech.com',
    },
  ];

  rewrite(url?: string | null): string | null | undefined {
    if (!url || typeof url !== 'string') {
      return url;
    }

    for (const rule of this.rewriteRules) {
      if (url.startsWith(rule.cdnPrefix)) {
        return url;
      }

      for (const legacyPrefix of rule.legacyPrefixes) {
        if (url.startsWith(legacyPrefix)) {
          return `${rule.cdnPrefix}${url.substring(legacyPrefix.length)}`;
        }
      }
    }

    return url;
  }

  rewriteDeep<T>(value: T): T {
    return this.rewriteUnknown(value) as T;
  }

  /**
   * Rewrite any legacy URLs stored before this shim was introduced so that
   * UI elements using local/session storage immediately benefit from CDN
   * routing without forcing users to re-login. Safe to remove after backend
   * starts serving CDN URLs.
   */
  rewriteBrowserStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.rewriteStorage(window.localStorage);
    this.rewriteStorage(window.sessionStorage);
  }

  private rewriteUnknown(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      const rewritten = this.rewrite(value);
      return rewritten ? rewritten : value;
    }

    if (Array.isArray(value)) {
      let mutated = false;
      const rewrittenArray = value.map((item) => {
        const rewritten = this.rewriteUnknown(item);
        mutated = mutated || rewritten !== item;
        return rewritten;
      });
      return mutated ? rewrittenArray : value;
    }

    if (this.shouldRewriteObject(value)) {
      let mutated = false;
      const clone: Record<string, unknown> = {};

      Object.keys(value as Record<string, unknown>).forEach((key) => {
        const currentValue = (value as Record<string, unknown>)[key];
        const rewrittenValue = this.rewriteUnknown(currentValue);
        mutated = mutated || rewrittenValue !== currentValue;
        clone[key] = rewrittenValue;
      });

      return mutated ? clone : value;
    }

    return value;
  }

  private shouldRewriteObject(
    value: unknown,
  ): value is Record<string, unknown> {
    if (!value || typeof value !== 'object') {
      return false;
    }

    if (
      value instanceof Date ||
      value instanceof Blob ||
      value instanceof ArrayBuffer ||
      value instanceof File ||
      (typeof Response !== 'undefined' && value instanceof Response) ||
      (typeof FormData !== 'undefined' && value instanceof FormData)
    ) {
      return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  }

  private rewriteStorage(storage?: Storage): void {
    if (!storage) {
      return;
    }

    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key) {
        keys.push(key);
      }
    }

    keys.forEach((key) => {
      const currentValue = storage.getItem(key);
      if (currentValue === null) {
        return;
      }

      const rewrittenValue = this.rewriteStoredValue(currentValue);
      if (rewrittenValue !== null) {
        storage.setItem(key, rewrittenValue);
      }
    });
  }

  private rewriteStoredValue(value: string): string | null {
    try {
      const parsed = JSON.parse(value);
      const rewritten = this.rewriteDeep(parsed);
      if (rewritten !== parsed) {
        return JSON.stringify(rewritten);
      }
    } catch {
      const rewritten = this.rewrite(value);
      if (typeof rewritten === 'string' && rewritten !== value) {
        return rewritten;
      }
    }

    return null;
  }
}
